import type { StylesConfig } from 'react-select';

// Colors reference the app-wide CSS custom properties defined in _tokens.scss,
// so react-select follows the same brand palette (incl. dark theme) as the rest
// of the app. The browser resolves var(--x) at render against :root.
const focusBorder = 'var(--primary)';
const borderColor = 'var(--border)';
const menuBorder = 'var(--border)';
const menuShadow = 'var(--elevation-2)';
const surfaceBg = 'var(--surface-elevated)';
const hoverBg = 'var(--surface-alt)';
const selectedBg = 'var(--primary-tint)';
const textColor = 'var(--text)';
const mutedColor = 'var(--text-muted)';

// Shape tokens (MD3-inspired) — mirror --radius-sm / --radius-md in _tokens.scss
const shapeSm = 8; // control radius (field)
const shapeMd = 12; // menu-panel radius (matches custom dropdown panels)

export const multiSelectStyles: StylesConfig = {
  control: (base, state) => ({
    ...base,
    backgroundColor: surfaceBg,
    minHeight: 'var(--field-height)',
    borderRadius: shapeSm,
    borderColor: state.isFocused ? focusBorder : borderColor,
    borderWidth: state.isFocused ? 2 : 1,
    boxShadow: state.isFocused ? 'var(--focus-ring)' : 'none',
    fontSize: 16,
    '&:hover': {
      borderColor: focusBorder,
    },
  }),
  input: (base) => ({
    ...base,
    color: textColor,
  }),
  singleValue: (base) => ({
    ...base,
    color: textColor,
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: surfaceBg,
    borderRadius: shapeMd,
    border: `1px solid ${menuBorder}`,
    boxShadow: menuShadow,
    zIndex: 1000,
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? hoverBg : 'transparent',
    color: textColor,
    cursor: 'pointer',
    padding: '6px 8px',
    '&:active': {
      backgroundColor: selectedBg,
    },
  }),
  placeholder: (base) => ({
    ...base,
    color: mutedColor,
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  clearIndicator: (base) => ({
    ...base,
    padding: '0 4px',
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: '0 8px',
    color: mutedColor,
  }),
  multiValue: (base) => ({
    ...base,
    display: 'none',
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '2px 8px',
  }),
};

export const singleSelectStyles: StylesConfig = {
  control: (base, state) => ({
    ...base,
    backgroundColor: surfaceBg,
    minHeight: 'var(--field-height-compact)',
    borderRadius: shapeSm,
    borderColor: state.isFocused ? focusBorder : borderColor,
    boxShadow: state.isFocused ? 'var(--focus-ring)' : 'none',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    '&:hover': {
      borderColor: focusBorder,
      backgroundColor: hoverBg,
    },
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: surfaceBg,
    border: `1px solid ${menuBorder}`,
    boxShadow: menuShadow,
    borderRadius: shapeMd,
    zIndex: 1000,
    minWidth: '100%',
    width: 150,
  }),
  option: (base, state) => {
    let bg = 'transparent';
    if (state.isSelected) bg = selectedBg;
    else if (state.isFocused) bg = hoverBg;
    return {
    ...base,
    backgroundColor: bg,
    color: state.isSelected ? focusBorder : textColor,
    fontWeight: state.isSelected ? 500 : 'normal',
    cursor: 'pointer',
    fontSize: 13,
    padding: '8px 12px',
    '&:active': {
      backgroundColor: selectedBg,
    },
    };
  },
  singleValue: (base) => ({
    ...base,
    color: textColor,
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: '0 6px',
    color: mutedColor,
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '0 8px',
  }),
};
